// KEY-559: the pure half of the section / whole-dial Discussion tab.
//
// The server hands back one flat, newest-first list of messages, each carrying the section, action point
// and item it was said on. Read that way a transcript is a feed with no sense of place, so this module
// regroups it the way the dial itself is laid out: one group per action point, in the dial's own structure
// order, each group reading oldest-first like the thread it came from.
//
// KEY-567: on a Group Key Dial the whole-dial transcript also carries its child dials' discussion. Children of
// one group so often share a definition that their section and action point keys collide, so a node is the
// dial AND its keys, and each child is named and ordered by its own structure, which the server sends along.
//
// Kept free of React so the ordering and naming rules can be pinned by plain unit tests.

import type { MessageDto, TranscriptChildDial, TranscriptMessage } from "@keydials/api-client";
import { sectionTitle } from "../dialModel";
import type { SegmentNoun } from "../vocabulary";

/** The parts of a dial's structure a transcript needs to place and name a message. Both
 *  `InstanceDetail.sections` and the view model's sections satisfy it, and so does a child dial's. */
export interface TranscriptStructureSection {
  key: string;
  label: string;
  showMnemonic?: boolean;
  segments: { key: string; name: string; items?: { key: string; name: string }[] | null }[];
}

/** One message in a group, with the item it was left on when it wasn't the action point's own thread. */
export interface TranscriptRow {
  message: MessageDto;
  /** The item's name, or its key when the structure no longer names it; null on the action point's own thread. */
  itemName: string | null;
}

/** Everything said on one action point, oldest-first. */
export interface TranscriptGroup {
  /** The dial it was said on: the one being read, or one of a group's child dials (KEY-567). */
  instanceId: string;
  /** That child dial's name; null on the dial being read. */
  dialName: string | null;
  sectionKey: string;
  segmentKey: string;
  /** "Section › Action point" by name. A key stands in for any part the structure doesn't name — a node
   *  renamed away, or removed since the conversation started — so a message is never left unlabelled. */
  label: string;
  rows: TranscriptRow[];
}

interface StructureIndex {
  order: Map<string, number>;
  names: Map<string, { label: string; items: Map<string, string> }>;
  sectionLabels: Map<string, string>;
}

const nodeId = (sectionKey: string, segmentKey: string) => `${sectionKey}\0${segmentKey}`;

function indexStructure(sections: readonly TranscriptStructureSection[]): StructureIndex {
  const order = new Map<string, number>();
  const names = new Map<string, { label: string; items: Map<string, string> }>();
  for (const section of sections) {
    for (const segment of section.segments) {
      const id = nodeId(section.key, segment.key);
      order.set(id, order.size);
      names.set(id, {
        label: `${sectionTitle(section)} › ${segment.name || segment.key}`,
        items: new Map((segment.items ?? []).map((i) => [i.key, i.name])),
      });
    }
  }
  return { order, names, sectionLabels: new Map(sections.map((s) => [s.key, sectionTitle(s)])) };
}

/**
 * Groups a transcript by action point in the dial's structure order: the dial being read first, then each
 * child dial in the order the server listed them, and any child it didn't describe after those, by name.
 * Within a dial, action points its structure doesn't contain follow the ones it does, ordered by key, so
 * nothing the server returned is ever dropped here — the server has already decided what this reader may
 * see, and a client that also filtered would only be able to get that wrong.
 */
export function groupTranscript(
  messages: readonly TranscriptMessage[],
  sections: readonly TranscriptStructureSection[],
  instanceId: string,
  childDials: readonly TranscriptChildDial[] = [],
): TranscriptGroup[] {
  const dialRank = new Map<string, number>([[instanceId, 0]]);
  const structures = new Map<string, readonly TranscriptStructureSection[]>([[instanceId, sections]]);
  for (const child of childDials) {
    if (!dialRank.has(child.instanceId)) {
      dialRank.set(child.instanceId, dialRank.size);
      structures.set(child.instanceId, child.sections);
    }
  }
  const indexes = new Map<string, StructureIndex>();
  const indexFor = (dialId: string) => {
    let index = indexes.get(dialId);
    if (!index) {
      index = indexStructure(structures.get(dialId) ?? []);
      indexes.set(dialId, index);
    }
    return index;
  };

  const groups = new Map<string, TranscriptGroup>();
  // The server's order is newest-first; walking it backwards makes equal timestamps come out in the order
  // they were said once the stable sort below runs.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    // A server from before KEY-567 names no dial, and everything it sends is the dial being read.
    const owner = m.instanceId || instanceId;
    const index = indexFor(owner);
    const named = index.names.get(nodeId(m.sectionKey, m.segmentKey));
    const id = `${owner}\0${nodeId(m.sectionKey, m.segmentKey)}`;
    let group = groups.get(id);
    if (!group) {
      group = {
        instanceId: owner,
        dialName: owner === instanceId ? null : m.instanceName || null,
        sectionKey: m.sectionKey,
        segmentKey: m.segmentKey,
        label: named?.label ?? `${index.sectionLabels.get(m.sectionKey) ?? m.sectionKey} › ${m.segmentKey}`,
        rows: [],
      };
      groups.set(id, group);
    }
    group.rows.push({
      message: m.message,
      itemName: m.itemKey ? (named?.items.get(m.itemKey) ?? m.itemKey) : null,
    });
  }

  const time = (row: TranscriptRow) => {
    const t = Date.parse(row.message.createdAtUtc);
    return Number.isNaN(t) ? 0 : t;
  };
  for (const group of groups.values()) {
    group.rows.sort((a, b) => time(a) - time(b));
  }

  const rankOf = (group: TranscriptGroup) => dialRank.get(group.instanceId) ?? Number.POSITIVE_INFINITY;
  return [...groups.values()].sort((a, b) => {
    const ar = rankOf(a);
    const br = rankOf(b);
    if (ar !== br) return ar - br;
    if (a.instanceId !== b.instanceId) {
      return (a.dialName ?? "").localeCompare(b.dialName ?? "") || a.instanceId.localeCompare(b.instanceId);
    }
    const { order } = indexFor(a.instanceId);
    const ai = order.get(nodeId(a.sectionKey, a.segmentKey));
    const bi = order.get(nodeId(b.sectionKey, b.segmentKey));
    if (ai !== undefined && bi !== undefined) return ai - bi;
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    return a.sectionKey.localeCompare(b.sectionKey) || a.segmentKey.localeCompare(b.segmentKey);
  });
}

/** "5 messages across 2 action points" — in this dial's own words for an action point (KEY-539). */
export function transcriptSummary(groups: readonly TranscriptGroup[], noun: SegmentNoun): string {
  const count = groups.reduce((n, g) => n + g.rows.length, 0);
  const places = groups.length;
  return `${count} ${count === 1 ? "message" : "messages"} ${places === 1 ? "in" : "across"} ${places} ${places === 1 ? noun.one : noun.many}`;
}
